/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's opyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

import type { FormErrors, FormValues } from '@shared/types';

/**
 * clearRequiredErrorFlag function.
 *
 * @param {*} errors - Parameter description
 * @param {*} fieldName - Parameter description
 *
 * @returns {*} Return value description
 */
export function clearRequiredErrorFlag(errors: FormErrors, fieldName: string): void {
  if (errors[fieldName] === '__REQUIRED__') {
    errors[fieldName] = undefined;
  }
}

/**
 * Builds the validation-related slice of the form store actions.
 *
 * @param get - Zustand store getter.
 * @returns Validation action methods bound to the store.
 */
export const createValidationSlice = (get: () => any) => ({
  isFieldEmpty: (value: any): boolean => {
    if (Array.isArray(value)) {
      return value.length === 0;
    }
    return !value || value === "" || value === false;
  },

  handleFieldValidationError: (field: string, setFieldError: (field: string, error?: string) => void): boolean => {
    setFieldError(field, '__REQUIRED__');
    return false;
  },

  clearFieldErrorIfNeeded: (field: string, errors: FormErrors, setFieldError: (field: string, error?: string) => void): void => {
    if (errors[field] === '__REQUIRED__') {
      setFieldError(field, undefined);
    }
  },

  getFirstMissingField: (formValues: FormValues, required: string[]): string | undefined => {
    for (const field of required) {
      const value = formValues[field];
      if (get().isFieldEmpty?.(value)) {
        return field;
      }
    }
    return undefined;
  },

  validateRequiredFields: (formValues: FormValues, required: string[], setFieldError: (field: string, error?: string) => void, formErrors: FormErrors): boolean => {
    let allValid = true;
    const { isFieldEmpty, handleFieldValidationError, clearFieldErrorIfNeeded } = get();

    for (const field of required) {
      const value = formValues[field];
      if (isFieldEmpty?.(value)) {
        handleFieldValidationError?.(field, setFieldError);
        allValid = false;
        continue;
      }

      clearFieldErrorIfNeeded?.(field, formErrors, setFieldError);
    }

    return allValid;
  },

  hasRemainingErrors: (errors: FormErrors): boolean => {
    return Object.values(errors).some((field) => field !== undefined);
  },

  validateSchema: (values: FormValues, errors: FormErrors): { isValid: boolean; errors: FormErrors } => {
    const { _schema } = get();
    if (!_schema) return { isValid: true, errors };

    const newErrors = { ...errors };
    try {
      _schema.parse(values);
      return { isValid: true, errors: newErrors };
    } catch (err: any) {
      if (err.errors) {
        err.errors.forEach((e: any) => {
          newErrors[e.path[0]] = e.message;
        });
      }
      return { isValid: false, errors: newErrors };
    }
  },

  validateRequiredFieldsInStore: (values: FormValues, errors: FormErrors): { isValid: boolean; errors: FormErrors } => {
    const { required, validateSingleFieldRequired } = get();
    const requiredFields = required ?? [];
    if (requiredFields.length === 0) return { isValid: true, errors };

    let allValid = true;
    let currentErrors = { ...errors };

    for (const field of requiredFields) {
      const result = validateSingleFieldRequired!(field, values[field], currentErrors);
      if (!result.isValid) allValid = false;
      currentErrors = result.errors;
    }
    return { isValid: allValid, errors: currentErrors };
  },

  validateSingleFieldRequired: (fieldName: string, value: any, errors: FormErrors): { isValid: boolean; errors: FormErrors } => {
    const { formStates, isFieldEmpty } = get();
    const newErrors = { ...errors };
    const isDisabled = formStates[fieldName]?.isDisabled;

    if (isDisabled) {
      clearRequiredErrorFlag(newErrors, fieldName);
      return { isValid: true, errors: newErrors };
    }

    if (isFieldEmpty?.(value)) {
      newErrors[fieldName] = '__REQUIRED__';
      return { isValid: false, errors: newErrors };
    }

    clearRequiredErrorFlag(newErrors, fieldName);
    return { isValid: true, errors: newErrors };
  },

  getFirstMissingFieldForScroll: (): string | undefined => {
    const { formValues, required, formErrors, _schema } = get();

    if (_schema) {
      const errorFields = Object.keys(formErrors).filter(field => formErrors[field] !== undefined);
      return errorFields.length > 0 ? errorFields[0] : undefined;
    }

    const firstMissing = get().getFirstMissingField?.(formValues, required);
    if (firstMissing) {
      return firstMissing;
    }

    const errorFields = Object.keys(formErrors).filter(field => formErrors[field] !== undefined);
    return errorFields.length > 0 ? errorFields[0] : undefined;
  },
});
